<template>
  <v-expansion-panels popout v-model="openPanel" accordion tile>
    <v-expansion-panel
      v-for="(payableByBusinessEntity, i) in payablesByAccount"
      :key="payableByBusinessEntity.businessEntityName"
    >
      <v-expansion-panel-header @click="assignTableHeightWithDelay">
        {{ payableByBusinessEntity.businessEntityName }}
        <v-spacer/>
        <template #actions>
          {{ formatMoney(payableByBusinessEntity.amount) }}
          <v-icon class="ml-2"
            >mdi-chevron-down</v-icon
          >
        </template>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-data-table
          :id="'payable-expansion-panel-table-'.concat(i)"
          v-if="payableByBusinessEntity.payables.length > 0"
          :headers="headers"
          :items.sync="payableByBusinessEntity.payables"
          :hide-default-footer="payableByBusinessEntity.payables.length < 10"
          dense
          :height="tableHeight"
          @update:options="tableOptionsUpdated"
          :item-class="rowClass"
        >
          <template v-slot:item.amount="{ item }">{{
            formatMoney(item.amount)
          }}</template>
          <template v-slot:item.grossAmount="{ item }">{{
            formatMoney(item.grossAmount)
          }}</template>
          <template v-slot:item.recovered="{ item }">{{
            formatRecovered(item)
          }}</template>
          <template v-slot:item.rate="{ item }">
            <span>{{ formatMoney(item.computedAtRate) }}</span>
          </template>
          <template #item.payOn="{item}">{{
            payOn(item.computedAtPayOn)
          }}</template>
          <template #item.payBy="{item}">{{
            payBy(item.computedAtPayBy)
          }}</template>
          <template v-slot:item.createdAt="{ item }">{{
            utcToLocalDate(item.ticketCreatedAt)
          }}</template>
          <template #item.activityWithSplit="{item}">
            <Icon v-if="item.specialization === TemplateSpecialization.Penalty.value" :tooltipText="$t('penalty')" icon="weight-alert" :small="false"/>
            <span>
              {{ item.activityName }}
            </span>
            <span v-if="item.computedAtSplit">
              ({{ formatPercentage(item.computedAtSplit) }})
            </span>
            <Icon
              v-if="item.deductFromPayableId"
              icon="mdi-circle-multiple-outline"
              iconColor="success"
              small
              :tooltipText="deductionTooltip(item)"
              @mouseenter.native="setHoveredParentPayable(item.deductFromPayableId)"
              @mouseleave.native="clearHoveredParentPayable"
            />
              <Icon
              v-if="hasDeductingChildren(item)"
              icon="mdi-minus-circle-multiple-outline"
              iconColor="error"
              small
              :tooltipText="$t('paymentDeducted')"
              @mouseenter.native="setHoveredDeducting(item)"
              @mouseleave.native="clearHoveredDeducting"
            />
          </template>
          <template v-slot:item.actions="{ item }">
            <Icon
              iconColor="black"
              icon="mdi-information-outline"
              :disabled="item.recoveryMethod === 1"
              :tooltipText="$t('details')"
              @icon-clicked="viewPayable(item)"
            >
            </Icon>
          </template>
        </v-data-table>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { formatMoney, formatPercentage } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { ticketPayableHeaders } from '@/headers/Payable.js'
import { PayOn, PayBy, TemplateSpecialization } from '@/utils/Enumerations.js'
import { tableHeight } from '@/utils/componentHelpers.js'
import { mapGetters } from 'vuex'

export default {
  name: 'PayableExpansionPanels',

  props: {
    payables: {
      type: Array,
      required: true
    },
    ticket: {
      type: Object,
      required: false
    },
    contractMode: {
      type: Number,
      required: false,
      default: 0
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    TemplateSpecialization,
    openPanel: 0,
    currentTableOptions: {},
    tableHeight: 'auto',
    hoveredDeductingPayableIds: [],
    hoveredParentPayableId: null
  }),

  created () {
    this.openPanel = this.payablesByAccount?.length === 1 ? 0 : null
    this.assignTableHeightWithDelay()
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),

    headers () {
      const headers = ticketPayableHeaders(this.contractMode)
      headers.pop()
      return headers
    },

    payablesByAccount () {
      const payablesByAccount = []
      this.payables.forEach(p => {
        const entityIndex = payablesByAccount.findIndex(pbv => pbv.businessEntityName === p.businessEntityName)
        const mappedPayable = {
          ...p,
          activityWithSplit: {
            name: p.activityName,
            split: p.computedAtSplit
          }
        }
        if (entityIndex === -1) {
          payablesByAccount.push({
            businessEntityName: p.businessEntityName,
            amount: p.amount,
            payables: [mappedPayable]
          })
        } else {
          payablesByAccount[entityIndex].payables.push(mappedPayable)
          payablesByAccount[entityIndex].amount += p.amount
        }
      })

      return payablesByAccount
    }
  },

  watch: {
    topDialogState: {
      handler () {
        this.assignTableHeightWithDelay(150)
      },
      deep: true
    },

    '$vuetify.breakpoint.width' () { this.assignTableHeight() },
    '$vuetify.breakpoint.height' () { this.assignTableHeight() }
  },

  methods: {
    formatMoney,
    formatPercentage,
    utcToLocalDate,
    payOn: (x) => PayOn.fromInt(x),
    payBy: (x) => PayBy.fromInt(x),

    hasDeductingChildren (item) {
      const payableId = item.payableId
      return this.payables?.some(p => p?.deductFromPayableId === payableId)
    },

    getDeductingChildren (item) {
      return (this.payables || []).filter(p => p?.deductFromPayableId === item.payableId)
    },

    setHoveredDeducting (item) {
      this.hoveredDeductingPayableIds = this.getDeductingChildren(item).map(c => c.payableId).filter(id => id != null)
    },

    clearHoveredDeducting () {
      this.hoveredDeductingPayableIds = []
    },

    rowClass (item) {
      const classes = []
      if (this.hoveredDeductingPayableIds.includes(item.payableId)) classes.push('payable-row-red-tint')
      if (this.hoveredParentPayableId === item.payableId) classes.push('payable-row-green-tint')
      return classes.join(' ')
    },

    formatRecovered (item) {
      return item.specialization === TemplateSpecialization.Penalty.value
        ? this.$t('notApplicable')
        : item.grossAmount - item.amount === 0
          ? ''
          : formatMoney(item.grossAmount - item.amount)
    },

    tableOptionsUpdated (tableOptions) {
      this.currentTableOptions = tableOptions
    },

    assignTableHeightWithDelay (delay = 50) {
      setTimeout(() => {
        this.assignTableHeight()
      }, delay)
    },

    assignTableHeight () {
      this.tableHeight = tableHeight({
        tableElementId: 'payable-expansion-panel-table-'.concat(this.openPanel),
        offset: 600,
        dataLength: this.payablesByAccount?.length || 0
      })
    },

    setHoveredParentPayable (parentPayableId) {
      this.hoveredParentPayableId = parentPayableId
    },

    clearHoveredParentPayable () {
      this.hoveredParentPayableId = null
    },

    deductionTooltip (item) {
      const parent = (this.payables || []).find(p => p?.payableId === item.deductFromPayableId)
      const from = parent?.activityName || 'parent activity'
      return this.$t('deductsFrom', { from })
    }
  }
}
</script>

<style>
[id^="payable-expansion-panel-table-"] .v-data-table__wrapper table tbody tr.payable-row-red-tint > td {
  background-color: rgba(244, 67, 54, 0.12);
}

[id^="payable-expansion-panel-table-"] .v-data-table__wrapper table tbody tr.payable-row-green-tint > td {
  background-color: rgba(76, 175, 80, 0.12);
}
</style>
